import {PlayerStep} from '../models/PlayerStep';
import {GameState} from '../models/GameState';
import {PlayerPath} from '../models/PlayerPath';
import {PlayerAction} from '../models/PlayerAction';
import {PlayerAdventure} from '../models/PlayerAdventure';
import adventureService from './adventureService';
import gameStateService from './gameStateService';
import conditionHelper from '../helpers/ConditionHelper';
import sessionBridgeService from './sessionBridgeService';
import {Platform} from 'react-native';

class PlayerService {
  /**
   * Charge une PlayerAdventure complète et retourne l'étape de départ
   * Identique à Flutter PlayerService.loadPlayerAdventure
   */
  async loadPlayerAdventure(uniqueURI: string): Promise<PlayerStep> {
    console.log(`[PlayerService] Loading adventure: ${uniqueURI}`);
    const adventure = await adventureService.loadPlayerAdventure(uniqueURI);
    console.log(`[PlayerService] Adventure loaded:`);
    console.log(`[PlayerService] - Steps: ${adventure.steps.map((s) => s.numero)}`);
    console.log(`[PlayerService] - Start number: ${adventure.startNumero}`);

    const startStep = adventure.steps.find((step) => step.numero === adventure.startNumero);
    if (!startStep) {
      throw new Error(`Start step ${adventure.startNumero} not found`);
    }

    return startStep;
  }

  async getGameState(adventureURI: string, slot: number): Promise<GameState | null> {
    return gameStateService.getGameStateByAdventureAndSlot(adventureURI, slot);
  }

  /**
   * Récupère l'étape actuelle depuis le gameState
   * Identique à Flutter PlayerService.getCurrentStep
   */
  async getCurrentStep(gameState: GameState): Promise<PlayerStep | null> {
    console.log(`[PlayerService] Getting current step`);
    console.log(`[PlayerService] - uniqueURI: ${gameState.uniqueURI}`);
    console.log(`[PlayerService] - currentStepNumber: ${gameState.data.currentStepNumber}`);

    try {
      if (!gameState.data.currentStepNumber) {
        console.error('[PlayerService] No current step number in game state');
        return null;
      }

      // EN MODE ÉDITION : utiliser loadEditingPlayerStep (API) au lieu de loadPlayerStep (DB locale)
      const isEditingMode = Platform.OS === 'web' && typeof window !== 'undefined' && (window.opener !== null || sessionBridgeService.isInPopup());
      
      let step: PlayerStep | null;
      if (isEditingMode) {
        console.log(`[PlayerService] getCurrentStep - Editing mode detected, loading step from API`);
        step = await adventureService.loadEditingPlayerStep(gameState.uniqueURI, gameState.data.currentStepNumber);
      } else {
        console.log(`[PlayerService] getCurrentStep - Normal mode, loading step from local DB`);
        step = await adventureService.loadPlayerStep(gameState.uniqueURI, gameState.data.currentStepNumber);
      }

      if (!step) {
        console.error(`[PlayerService] Failed to load step ${gameState.data.currentStepNumber}`);
        return null;
      }

      // Attacher le gameState à l'étape
      const stepWithGameState: PlayerStep = {
        ...step,
        gameState,
      };

      console.log(`[PlayerService] Step loaded successfully:`);
      console.log(`[PlayerService] - numero: ${stepWithGameState.numero}`);
      console.log(`[PlayerService] - paths: ${stepWithGameState.paths.length}`);
      console.log(`[PlayerService] - actions: ${stepWithGameState.actions.length}`);

      return stepWithGameState;
    } catch (error) {
      console.error('[PlayerService] Failed to get current step:', error);
      throw error;
    }
  }

  /**
   * Charge un PlayerStep spécifique depuis une aventure
   * Identique à Flutter PlayerService.loadPlayerStep
   */
  async loadPlayerStep(uniqueURI: string, stepNumber: number): Promise<PlayerStep> {
    console.log(`[PlayerService] Loading step ${stepNumber} for adventure ${uniqueURI}`);
    
    // EN MODE ÉDITION : utiliser loadEditingPlayerStep (API) au lieu de loadPlayerStep (DB locale)
    const isEditingMode = Platform.OS === 'web' && typeof window !== 'undefined' && (window.opener !== null || sessionBridgeService.isInPopup());
    
    let step: PlayerStep;
    if (isEditingMode) {
      console.log(`[PlayerService] loadPlayerStep - Editing mode detected, loading step from API`);
      step = await adventureService.loadEditingPlayerStep(uniqueURI, stepNumber);
    } else {
      console.log(`[PlayerService] loadPlayerStep - Normal mode, loading step from local DB`);
      step = await adventureService.loadPlayerStep(uniqueURI, stepNumber);
    }
    
    console.log(`[PlayerService] Step loaded:`);
    console.log(`[PlayerService] - Number: ${step.numero}`);
    console.log(`[PlayerService] - Paths: ${step.paths.map((p) => p.child)}`);
    console.log(`[PlayerService] - Actions: ${step.actions.map((a) => a.id)}`);
    console.log(`[PlayerService] - Attributes: ${step.attributes.length}`);
    return step;
  }

  /**
   * Choisit un chemin (par index) et retourne la nouvelle étape
   * Identique à Flutter PlayerService.choosePath
   */
  async choosePath(gameState: GameState, pathIndex: number, editorMode: boolean = false, currentStepFromContext?: PlayerStep): Promise<PlayerStep> {
    console.log(`[PlayerService] choosePath - pathIndex: ${pathIndex}, editorMode: ${editorMode}`);
    console.log(`[PlayerService] choosePath - gameState.uniqueURI: ${gameState.uniqueURI}, slot: ${gameState.slot}`);
    console.log(`[PlayerService] choosePath - gameState.data.currentStepNumber: ${gameState.data.currentStepNumber}`);
    console.log(`[PlayerService] choosePath - currentStepFromContext provided: ${!!currentStepFromContext}`);

    // Utiliser le currentStep du contexte si fourni, sinon le recharger
    // Cela garantit que le path à l'index correspond bien au path affiché
    let currentStep: PlayerStep;
    if (currentStepFromContext) {
      console.log(`[PlayerService] choosePath - Using currentStep from context: numero=${currentStepFromContext.numero}, paths count=${currentStepFromContext.paths.length}`);
      currentStep = currentStepFromContext;
    } else {
      console.log(`[PlayerService] choosePath - Reloading currentStep from DB`);
      const reloadedStep = await this.getCurrentStep(gameState);
      if (!reloadedStep) {
        throw new Error('No current step');
      }
      currentStep = reloadedStep;
      console.log(`[PlayerService] choosePath - Current step loaded: numero=${currentStep.numero}, paths count=${currentStep.paths.length}`);
    }

    // En mode éditeur, utiliser allPaths (tous les paths originaux) pour la validation
    // Sinon, utiliser paths (paths filtrés)
    const pathsToValidate = editorMode && currentStep.allPaths 
      ? currentStep.allPaths 
      : currentStep.paths;
    
    console.log(`[PlayerService] choosePath - Using ${editorMode && currentStep.allPaths ? 'allPaths' : 'paths'} for validation (length: ${pathsToValidate.length})`);

    if (pathIndex < 0 || pathIndex >= pathsToValidate.length) {
      console.error(`[PlayerService] choosePath - Invalid path index: ${pathIndex} (valid range: 0-${pathsToValidate.length - 1})`);
      throw new Error('Invalid path index');
    }

    const path = pathsToValidate[pathIndex];
    console.log(`[PlayerService] choosePath - Selected path at index ${pathIndex}:`, {
      index: pathIndex,
      id: path.id,
      description: path.description?.substring(0, 100),
      child: path.child,
      conditions: path.conditions,
      conditionsCount: path.conditions?.length || 0,
    });
    console.log(`[PlayerService] choosePath - All paths in currentStep (reloaded):`, currentStep.paths.map((p, i) => ({
      index: i,
      id: p.id,
      description: p.description?.substring(0, 50),
      child: p.child,
      conditions: p.conditions,
      conditionsCount: p.conditions?.length || 0,
    })));
    
    // VÉRIFICATION CRITIQUE : S'assurer que le path à l'index pathIndex correspond bien au path attendu
    // Le path pourrait être différent si l'ordre a changé ou si l'étape a été modifiée
    if (!path) {
      console.error(`[PlayerService] choosePath - Path is null at index ${pathIndex}!`);
      throw new Error(`Path not found at index ${pathIndex}`);
    }

    // Vérifier les conditions du path (sauf en mode cheat)
    // IMPORTANT: Utiliser le même gameState que celui utilisé pour filtrer les paths dans BaseStepWidget
    let conditionResult = true; // Par défaut, accepter le path
    if (!editorMode && path.conditions && path.conditions.length > 0) {
      console.log(`[PlayerService] choosePath - Path conditions:`, path.conditions);
      console.log(`[PlayerService] choosePath - Checking conditions with gameState:`, {
        uniqueURI: gameState.uniqueURI,
        slot: gameState.slot,
        currentStepNumber: gameState.data.currentStepNumber,
        objects: gameState.data.objects?.map(o => ({id: o.attributeId, dropped: o.dropped, value: o.value})) || [],
        skills: gameState.data.skills?.map(s => ({id: s.attributeId, value: s.value})) || [],
        adventureExtraFields: gameState.data.adventureExtraFields?.map(f => ({type: f.type, currentValue: f.currentValue, initValue: f.initValue})) || [],
      });
      console.log(`[PlayerService] choosePath - Checking conditions with currentStep:`, {
        numero: currentStep.numero,
        pathsCount: currentStep.paths.length,
        pathIndex: pathIndex,
        pathId: path.id,
        pathDescription: path.description?.substring(0, 50),
      });
      console.log(`[PlayerService] choosePath - About to check conditions with conditionHelper.checkConditions`);
      conditionResult = conditionHelper.checkConditions(gameState, currentStep, path.conditions);
      console.log(`[PlayerService] choosePath - Condition result from conditionHelper: ${conditionResult}`);
      if (!conditionResult) {
        console.error(`[PlayerService] choosePath - Condition check failed!`);
        console.error(`[PlayerService] choosePath - Path conditions:`, JSON.stringify(path.conditions, null, 2));
        console.error(`[PlayerService] choosePath - GameState used for check:`, JSON.stringify({
          uniqueURI: gameState.uniqueURI,
          slot: gameState.slot,
          currentStepNumber: gameState.data.currentStepNumber,
          objects: gameState.data.objects,
          skills: gameState.data.skills,
          adventureExtraFields: gameState.data.adventureExtraFields,
        }, null, 2));
      }
    } else if (editorMode) {
      console.log(`[PlayerService] choosePath - Editor mode enabled, skipping condition check`);
    } else {
      console.log(`[PlayerService] choosePath - No conditions, path is always available`);
    }

    if (!conditionResult) {
      console.error(`[PlayerService] choosePath - Path conditions not met for path ${pathIndex}`);
      console.error(`[PlayerService] choosePath - Path details:`, {
        id: path.id,
        description: path.description,
        conditions: path.conditions,
      });
      console.error(`[PlayerService] choosePath - GameState snapshot:`, {
        uniqueURI: gameState.uniqueURI,
        slot: gameState.slot,
        currentStepNumber: gameState.data.currentStepNumber,
        objects: gameState.data.objects?.map(o => ({id: o.attributeId, dropped: o.dropped})) || [],
        skills: gameState.data.skills?.map(s => ({id: s.attributeId, value: s.value})) || [],
      });
      throw new Error('Path conditions not met');
    }

    // Charger la nouvelle étape
    // EN MODE ÉDITION : utiliser loadEditingPlayerStep (API) au lieu de loadPlayerStep (DB locale)
    // Détecter le mode édition via window.opener (popup) ou sessionBridgeService
    const isEditingMode = Platform.OS === 'web' && typeof window !== 'undefined' && (window.opener !== null || sessionBridgeService.isInPopup());
    
    let newStep: PlayerStep;
    if (isEditingMode) {
      console.log(`[PlayerService] choosePath - Editing mode detected, loading step from API`);
      newStep = await adventureService.loadEditingPlayerStep(gameState.uniqueURI, path.child);
    } else {
      console.log(`[PlayerService] choosePath - Normal mode, loading step from local DB`);
      newStep = await adventureService.loadPlayerStep(gameState.uniqueURI, path.child);
    }
    console.log(`[PlayerService] choosePath - New step loaded: ${newStep.numero}`);

    return newStep;
  }

  /**
   * Met à jour le gameState dans la base de données
   */
  async updateGameState(gameState: GameState): Promise<void> {
    await gameStateService.updateGameState(gameState);
  }

  /**
   * Met à jour un attribut dans le gameState
   */
  async updateAttribute(gameState: GameState, attributeId: string, value: string): Promise<void> {
    const existingIndex = gameState.data.attributes.findIndex(
      (attr) => attr.attributeId === attributeId,
    );

    const updatedAttributes = [...gameState.data.attributes];
    if (existingIndex >= 0) {
      updatedAttributes[existingIndex] = {
        ...updatedAttributes[existingIndex],
        value,
      };
    } else {
      updatedAttributes.push({
        attributeId,
        value,
        dropped: 0,
      });
    }

    const updatedData = {
      ...gameState.data,
      attributes: updatedAttributes,
    };

    const updatedGameState: GameState = {
      ...gameState,
      data: updatedData,
      dirty: true,
      lastUpdate: new Date().toISOString(),
    };

    await this.updateGameState(updatedGameState);
  }
}

export default new PlayerService();
